// The five ways a commission worksheet can be marked paid.
//
// WHY THIS EXISTS: commission_disbursements.paid_method has a CHECK that
// accepts only these five values. "Mark paid" used to take free text, so
// "Payroll" or "Gusto" was refused by the database and the page swallowed
// the failure (Sentry JAVASCRIPT-REACT-5M, 2026-10-30). The page picks from
// this list and the server checks against it, so both agree by construction.

export const PAID_METHODS = ["check", "wire", "ach", "payroll", "other"] as const;
export type PaidMethod = (typeof PAID_METHODS)[number];

/** Plain labels for the picker. */
export const PAID_METHOD_LABELS: Record<PaidMethod, string> = {
  check: "Check",
  wire: "Wire",
  ach: "ACH",
  payroll: "Payroll (Gusto)",
  other: "Other",
};

/**
 * Trim and lowercase, then accept only a known method. Returns null for
 * anything else so the caller can refuse with a sentence, not a database error.
 * A missing method means "check", the long-standing default.
 */
export function normalizePaidMethod(raw: unknown): PaidMethod | null {
  if (raw === undefined || raw === null || raw === "") return "check";
  if (typeof raw !== "string") return null;
  const cleaned = raw.trim().toLowerCase();
  return (PAID_METHODS as readonly string[]).includes(cleaned)
    ? (cleaned as PaidMethod)
    : null;
}

/** The sentence a 400 carries, naming the allowed choices. */
export const PAID_METHOD_REFUSAL =
  "That payment method isn't one we can record. Choose check, wire, ACH, payroll or other.";

/** Payroll when the person is paid through Gusto, otherwise check. */
export function defaultPaidMethod(paidThroughGusto: boolean): PaidMethod {
  return paidThroughGusto ? "payroll" : "check";
}
